package fr.frux.feature.map

import fr.frux.shared.domain.model.Place
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.update

internal const val SEARCH_DEBOUNCE_MS = 300L

/**
 * Saisie de recherche, son debounce, ses suggestions d'espece (#186) et ses lieux (#394). Porte
 * sa propre saisie brute ([MutableStateFlow]) plutot que de la lire dans [MapState] : la
 * republier a chaque touche dans l'etat ferait tourner le debounce a vide.
 */
internal class SpeciesSearchController(
    private val launchForUser: (suspend CoroutineScope.() -> Unit) -> Job,
    private val state: MutableStateFlow<MapState>,
    private val armFraming: () -> Unit,
    private val frameVisibleResults: suspend () -> Unit,
    private val searchPlaces: suspend (String) -> Result<List<Place>>,
) {
    /** Saisie de recherche brute, debouncee en aval. */
    private val searchQuery = MutableStateFlow("")

    /**
     * Debounce de la recherche (#53) : `mapLatest` annule la recherche en cours des qu'une frappe
     * arrive, la ou une coroutine par frappe laissait deux resultats concurrents se courir apres.
     *
     * Les resultats locaux (arbres, especes) sont publies avant l'appel reseau des lieux : ils
     * sont immediats, et la reponse du geocodeur ne doit pas les retenir. L'appel reseau vit dans
     * le meme `mapLatest`, donc une frappe suivante l'annule aussi.
     */
    @OptIn(FlowPreview::class, ExperimentalCoroutinesApi::class)
    fun start() {
        launchForUser {
            searchQuery
                .debounce(SEARCH_DEBOUNCE_MS)
                .mapLatest { query ->
                    publishLocalResults(query)
                    val placeQuery = query.trim()
                    if (placeQuery.length >= MIN_PLACE_QUERY_LENGTH) {
                        // Un echec est silencieux : la recherche locale reste utilisable, et un
                        // message pour un geocodeur en panne n'aiderait personne a chercher un arbre.
                        val places = searchPlaces(placeQuery).getOrDefault(emptyList())
                        // La saisie brute, pas MapState.searchQuery : choisir une espece y ecrit son
                        // nom, qui peut egaler la frappe. Une espece choisie ferme aussi les lieux.
                        if (searchQuery.value == query && state.value.selectedSpeciesRollupId == null) {
                            state.update { it.copy(placeResults = places) }
                        }
                    }
                }.collect {}
        }
    }

    private suspend fun publishLocalResults(query: String) {
        val results = matchingTrees(query, state.value.visibleTrees)
        val suggestions = matchingSpecies(query, state.value.species)
        // La saisie a pu repartir pendant la recherche : ne pas ecraser un etat plus recent.
        if (state.value.searchQuery != query) return
        state.update { it.copy(searchResults = results, speciesSuggestions = suggestions) }
        // Chercher, c'est demander a voir autre chose : la camera reprend la main sur les seuls
        // resultats. Effacer la recherche, en revanche, ne recadre pas — la camera resterait a se
        // replacer a chaque touche effacee.
        if (results.isNotEmpty()) {
            armFraming()
            frameVisibleResults()
        }
    }

    /**
     * La saisie est exposee immediatement (le champ doit suivre la frappe), la recherche elle-meme
     * part dans [start]. Une requete trop courte vide les resultats sans attendre le debounce :
     * effacer sa recherche doit nettoyer l'ecran tout de suite.
     */
    fun onQueryChange(query: String) {
        state.update {
            it.copy(
                searchQuery = query,
                searchResults = if (query.length < MIN_SEARCH_QUERY_LENGTH) emptyList() else it.searchResults,
                speciesSuggestions = if (query.length < MIN_SEARCH_QUERY_LENGTH) emptyList() else it.speciesSuggestions,
                placeResults = if (query.trim().length < MIN_PLACE_QUERY_LENGTH) emptyList() else it.placeResults,
                selectedSpeciesRollupId = null,
                // La frappe efface la selection d'espece : le bandeau attache a cette selection
                // ne doit pas lui survivre. Reevalue sur le lot complet, selection levee.
                filteredNoResults =
                    if (it.isLoading || it.selectedSpeciesRollupId == null) {
                        it.filteredNoResults
                    } else {
                        selectionNoResults(it.nearbyTrees, it.filter, selectedRollupId = null)
                    },
            )
        }
        searchQuery.value = query
    }

    /** Vide la saisie sans republier de resultats (selection d'espece, effacement de filtres, reset). */
    fun clear() {
        searchQuery.value = ""
    }
}
